import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Column, Spinner } from '@/components/core';
import PageLayout from '@/components/layout/PageLayout';
import { useAuth } from '@/contexts/AuthContext.tsx';
import AutoPlayProvider from '@/contexts/AutoPlayContext';
import TestChatProvider from '@/contexts/TestChatContext';
import Demo from '@/features/Demo/screens/Demo';
import DemoAccessExpired from '@/features/Demo/screens/DemoAccessExpired';
import Login from '@/features/Login/screens/Login';
import validateTestCode from '@/features/Login/services/validateTestCode.ts';
import OnboardingModal from '@/features/Onboarding/components/OnboardingModal';
import ListeningTest from '@/features/Onboarding/screens/ListeningTest';
import MicrophonePermission from '@/features/Onboarding/screens/MicrophonePermission';
import ReadyToIceBreaker from '@/features/Onboarding/screens/ReadyToIceBreaker';
import RecordingTest from '@/features/Onboarding/screens/RecordingTest';
import TimerTipPage from '@/features/Onboarding/screens/TimerTip';
import VideoTest from '@/features/Onboarding/screens/VideoTest';
import Welcome from '@/features/Onboarding/screens/Welcome';
import ExportResult from '@/features/Result/screens/ExportResult';
import ResultPage from '@/features/Result/screens/Result';
import Completed from '@/features/Testing/screens/Completed';
import Processing from '@/features/Testing/screens/Processing';
import TestChat from '@/features/Testing/screens/TestChat';
import { isDemoAccess } from '@/helpers/accessOrigin';
import { ROUTES } from '@/types/routes';


function ValidateStorage() {
  const navigate = useNavigate()
  const { handleTestCodeValidated, resetTestData } = useAuth()

  const code = sessionStorage.getItem('testCode') ?? null
  // Demo users never received a code, so the login screen would be a dead end
  const fallbackRoute = isDemoAccess() ? ROUTES.ACCESS_EXPIRED : ROUTES.LOGIN

  useEffect(() => {
    if (code) {
      validateTestCode({
        code
      })
        .then((response) => {
          handleTestCodeValidated(code, response)

          setTimeout(() => {
            navigate(
              response.showOnboardingQuestions
                ? ROUTES.ONBOARDING.ONBOARDING_SURVEY
                : ROUTES.ONBOARDING.WELCOME
            )
          }, 0)
        })
        .catch(() => {
          resetTestData()
          navigate(fallbackRoute, { replace: true })
        })
    } else {
      navigate(fallbackRoute, { replace: true })
    }
  }, [code])

  return (
    <Column className="h-screen w-screen items-center justify-center">
      <Spinner className="text-white h-16 w-16" />
    </Column>
  )
}

export function AppRouter() {
  const { student } = useAuth()

  if (student?._id) {
    return (
      <AutoPlayProvider>
        <BrowserRouter>
          <TestChatProvider>
            <Routes>
              <Route element={<PageLayout />}>
                <Route
                  path={ROUTES.ONBOARDING.ONBOARDING_SURVEY}
                  element={<OnboardingModal />}
                />
                <Route path={ROUTES.ONBOARDING.WELCOME} element={<Welcome />} />
                <Route
                  path={ROUTES.ONBOARDING.LISTENING_TEST}
                  element={<ListeningTest />}
                />
                <Route
                  path={ROUTES.ONBOARDING.MICROPHONE_PERMISSION}
                  element={<MicrophonePermission />}
                />
                <Route
                  path={ROUTES.ONBOARDING.RECORDING_TEST}
                  element={<RecordingTest />}
                />
                <Route
                  path={ROUTES.ONBOARDING.VIDEO_TEST}
                  element={<VideoTest />}
                />
                <Route
                  path={ROUTES.ONBOARDING.TIMER_TIP}
                  element={<TimerTipPage />}
                />
                <Route
                  path={ROUTES.ONBOARDING.READY_TO_START}
                  element={<ReadyToIceBreaker />}
                />

                <Route
                  path={ROUTES.TESTING.ICEBREAKER}
                  element={<TestChat />}
                />
                <Route path={ROUTES.TESTING.TEST} element={<TestChat />} />

                <Route path={ROUTES.PROCESSING} element={<Processing />} />
                <Route path={ROUTES.COMPLETED} element={<Completed />} />
              </Route>
              <Route path={ROUTES.RESULTS} element={<ResultPage />} />
              <Route path="/resultado" element={<ResultPage />} />
              <Route
                path="*"
                element={<Navigate to={ROUTES.ONBOARDING.WELCOME} replace />}
              />
            </Routes>
          </TestChatProvider>
        </BrowserRouter>
      </AutoPlayProvider>
    )
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path={ROUTES.LOGIN} element={<Login />} />
        <Route path={ROUTES.DEMO} element={<Demo />} />
        <Route
          path={ROUTES.ACCESS_EXPIRED}
          element={<DemoAccessExpired />}
        />

        <Route path={ROUTES.RESULTS} element={<ResultPage />} />
        <Route path="/resultado" element={<ResultPage />} />

        <Route path={ROUTES.RESULTS_EXPORT} element={<ExportResult />} />
        <Route path="/resultado/export" element={<ExportResult />} />

        <Route path="*" element={<ValidateStorage />} />
      </Routes>
    </BrowserRouter>
  )
}
